"use client";

import { Toaster, toast } from "react-hot-toast";

import { Button } from "./ui";

/**
 * The office's feedback: transient stacked toasts in the bottom-right
 * corner, backed by react-hot-toast (stacking, enter/exit animation,
 * hover-to-pause all come from the library). On-change controls call it
 * directly; button forms reach it through SavedNote and ErrorToast, so a
 * save or a failure never moves the page. A message names what it is about
 * ("Checklist saved."): a toast has no place in the page to say it for it.
 *
 *   …mount once per surface: <OfficeToaster />   (office layout has one)
 *   …on save: showToast("Saved.") / showToast(message, "error");
 *
 * `sticky` keeps a toast until its × dismisses it — for a failure whose
 * control is gone by the time anyone reads it (an item line removed or
 * rebuilt by the edit that failed). It stacks like any other, so it never
 * holds back the toasts after it. `id` names a toast so a repeat updates it
 * instead of stacking a twin, and so it can be dismissed by whoever raised
 * it (ErrorToast).
 */
export type ToastTone = "success" | "error";

const TOAST_MS = 5000;

export function showToast(
  message: string,
  tone: ToastTone = "success",
  { sticky = false, id }: { sticky?: boolean; id?: string } = {},
) {
  const make = tone === "error" ? toast.error : toast.success;
  // A failure's message is whatever failed said: a command line, a DSN, a
  // file path — runs with no space to break at. `wrap-anywhere` lets one
  // break where it must AND counts that toward the span's smallest width;
  // without the second part the message, a flex item, could not shrink
  // under its longest run, and pushed out through the card's edge.
  const text = (
    <span
      className={`min-w-0 font-mono text-xs wrap-anywhere ${
        tone === "error" ? "text-red-400" : "text-fg"
      }`}
    >
      {message}
    </span>
  );
  make(
    sticky
      ? (t) => (
          <span className="flex min-w-0 items-center gap-3">
            {text}
            <Button
              size="icon"
              aria-label="Dismiss"
              title="Dismiss this notification"
              onClick={() => toast.dismiss(t.id)}
            >
              ×
            </Button>
          </span>
        )
      : text,
    {
      ...(sticky ? { duration: Infinity } : {}),
      ...(id ? { id } : {}),
      icon: (
        <span
          aria-hidden
          className={`block h-1.5 w-1.5 shrink-0 rounded-full ${
            tone === "error" ? "bg-red-400" : "bg-emerald-400"
          }`}
        />
      ),
    },
  );
}

/**
 * A sticky toast that counts `seconds` down (re-rendered each tick), then
 * fires `onDone` (e.g. a navigation) — so a redirect is announced, not abrupt.
 * `build(n)` returns the message for `n` seconds remaining; clicking the toast
 * skips the wait. Returns a canceller that stops the countdown + dismisses.
 */
export function showCountdownToast(
  build: (secondsLeft: number) => string,
  seconds: number,
  onDone: () => void,
): () => void {
  const id = "countdown";
  let n = seconds;
  let done = false;

  const finish = () => {
    if (done) return;
    done = true;
    clearInterval(timer);
    toast.dismiss(id);
    onDone();
  };

  const render = () =>
    toast.success(
      <button
        type="button"
        onClick={finish}
        className="font-mono text-xs text-fg"
        title="Go now"
      >
        {build(n)}
      </button>,
      {
        id,
        duration: Infinity,
        icon: (
          <span
            aria-hidden
            className="block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400"
          />
        ),
      },
    );

  render();
  const timer = setInterval(() => {
    n -= 1;
    if (n <= 0) finish();
    else render();
  }, 1000);

  return () => {
    if (done) return;
    done = true;
    clearInterval(timer);
    toast.dismiss(id);
  };
}

/** The toast viewport — mounted once in the office dashboard layout. House
 *  card styling rides toastOptions; the tone dot replaces the default icons. */
export function OfficeToaster() {
  return (
    <Toaster
      position="bottom-right"
      toastOptions={{
        duration: TOAST_MS,
        style: {
          background: "var(--color-bg-muted)",
          color: "var(--color-fg)",
          border: "1px solid var(--color-line)",
          borderRadius: "0.5rem",
          padding: "10px 14px",
        },
      }}
    />
  );
}
